import React, {useState, useEffect} from 'react';
import {Helmet, HelmetProvider} from 'react-helmet-async';
import companyLogo from "../kantei-logo.svg";
import appLogo from "../kantei-mobile-logo.svg";
import backButton from "../back.svg";
import StreetViewMap from "../components/StreetViewMap";
import {Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis} from "recharts";
import PropertyMap from "../components/PropertyMap";
import {Viewer, Worker} from "@react-pdf-viewer/core";
import {ApolloClient, gql, HttpLink, InMemoryCache} from "@apollo/client";
import fetch from "cross-fetch";
import {FadeLoader} from 'react-spinners';
import {VIEW_MODES} from "../Const";
import {useParams} from "react-router-dom";
import { useAnalytics } from '../hooks/analytics/useAnalytics';
import { SCREEN_NAMES, BUTTON_NAMES } from '../constants/analytics';

function Home() {
    const {apiVersion} = useParams();
    // 認証画面で仕様
    const [authWarnMessage, setAuthWarnMessage] = useState('');

    // ホーム画面で使用
    const [memos, setMemos] = useState([]);
    const [enableMarketReport, setEnableMarketReport] = useState(false);
    const [enableClickAi, setEnableClickAi] = useState(false);
    const [enableMansionPriceAssessment, setEnableMansionPriseAssessment] = useState(false);

    // 相場レポートで使用
    const [sobaReportItem, setSobaReportItem] = useState({})
    const [data, setData] = useState([])
    const [data2, setData2] = useState([])

    // ポチッとAIで使用
    const [clickAiItem, setClickAiItem] = useState({})

    // ガッツリ査定で使用
    const [mansionAssessmentItem, setMansionAssessmentItem] = useState({})

    // 相場レポート・ポチッとAIで使用
    const [panoId, setPanoId] = useState('')

    // 査定書作成用
    const [authCodeData, setAuthCodeData] = useState('');
    const [noteRequestData, setNoteRequestData] = useState('');

    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState(VIEW_MODES.AUTH);

    // アナリティクスのフック
    const analytics = useAnalytics();

    // 初回マウント時にアナリティクスイベントを送信
    useEffect(() => {
        analytics.trackScreenView(SCREEN_NAMES.AUTH);
    }, []);

    // viewModeが変更されたときにアナリティクスイベントを送信
    useEffect(() => {
        // AUTH画面は初回マウント時のuseEffectで送信済みなのでスキップ
        if (viewMode === VIEW_MODES.AUTH) {
            return;
        }

        const screenNameMap = {
            [VIEW_MODES.HOME]: SCREEN_NAMES.HOME,
            [VIEW_MODES.REPORT]: SCREEN_NAMES.MARKET_REPORT,
            [VIEW_MODES.POTITTO]: SCREEN_NAMES.POTITTO_AI,
            [VIEW_MODES.MANSION_ASSESSMENT]: SCREEN_NAMES.MANSION_ASSESSMENT,
        };

        const screenName = screenNameMap[viewMode];
        if (screenName) {
            analytics.trackScreenView(screenName, {
                project_id: noteRequestData?.note_id,
            });
        }
    }, [viewMode]);

    // ガッツリ査定の査定書取得時に使用
    const [gatturiAssessmentId, setGatturiAssessmentId] = useState('');
    const CustomizedDotForLineChart = (props) => {
        const {cx, cy, stroke, payload, value, labelPosition} = props;

        if (labelPosition === 'up') {
            return (
                <>
                    <text x={cx - 15} y={cy - 10} fontSize="12">{value}</text>
                    <rect x={cx - 5} y={cy - 5} width={10} height={10} fill={stroke}/>
                </>
            );
        } else if (labelPosition === 'down') {
            return (
                <>
                    <circle cx={cx} cy={cy} r={6} fill={stroke}/>
                    <text x={cx - 15} y={cy + 20} fontSize="12">{value}</text>
                </>
            );
        } else {
            return (
                <>
                    <rect x={cx - 5} y={cy - 5} width={10} height={10} fill={stroke}/>
                </>
            );
        }
    };

    // GraphQLクライアント
    const apolloClient = new ApolloClient({
        link: new HttpLink({
            uri: process.env.REACT_APP_EXT_API_URL + apiVersion + '/',
            fetch
        }),
        cache: new InMemoryCache(),
    });

    return (
        <HelmetProvider>
            <Helmet>
                <title>カンテイモバイル</title>
            </Helmet>
            <div className="App">
                <div className="App-background">
                    <div className="App-base">
                        {loading && (
                            <div className="App-loading">
                                <FadeLoader
                                    color="#78D7FF"
                                    loading={loading}
                                    size={100}
                                />
                            </div>
                        )}
                        {viewMode === VIEW_MODES.AUTH && (
                            <>
                                <div className="App-header">
                                    <img src={companyLogo} className="Company-logo" alt="company-logo"/>
                                    <img src={appLogo} className="App-logo" alt="company-logo"/>
                                </div>
                                <div className="App-body">
                                    <div className="Auth-title">
                                        弊社担当者より共有しております<br/>
                                        4桁の閲覧用パスワードを入力してください。
                                    </div>
                                    <div className="Auth-number-container">
                                        <input type="tel" className="Auth-number" id="Auth-number-input" maxLength="4"/>
                                    </div>
                                    <div className="Auth-button-container">
                                        <button type="button" className="Auth-button" onClick={() => {
                                            analytics.trackButtonClick(BUTTON_NAMES.AUTH_SUBMIT, BUTTON_NAMES.AUTH_SUBMIT);
                                            clickAuth();
                                        }}>
                                            認 証
                                        </button>
                                    </div>
                                    <div className="Auth-warn-message-container">
                                            <span className="Auth-warn-message">
                                                {authWarnMessage}
                                            </span>
                                    </div>
                                </div>
                            </>
                        )}

                        {viewMode === VIEW_MODES.HOME && (
                            <>
                                <div className="App-header">
                                    <img src={companyLogo} className="Company-logo" alt="company-logo"/>
                                    <img src={appLogo} className="App-logo" alt="company-logo"/>
                                </div>
                                <div className="App-body">
                                    {enableMarketReport && (
                                        <div className="Home-button-container">
                                            <button type="button" className="Home-button" onClick={() => {
                                                analytics.trackButtonClick(BUTTON_NAMES.MARKET_REPORT, BUTTON_NAMES.MARKET_REPORT);
                                                setViewMode(VIEW_MODES.REPORT);
                                            }}>
                                                相場レポート
                                            </button>
                                        </div>
                                    )}
                                    {enableClickAi && (
                                        <div className="Home-button-container">
                                            <button type="button" className="Home-button" onClick={() => {
                                                analytics.trackButtonClick(BUTTON_NAMES.POTITTO_AI, BUTTON_NAMES.POTITTO_AI);
                                                setViewMode(VIEW_MODES.POTITTO);
                                            }}>
                                                ポチッとAI
                                            </button>
                                        </div>
                                    )}
                                    {/*
                                        <div className="Home-button-container">
                                            <button type="button" className="Home-button">
                                                サクット媒介契約
                                            </button>
                                        </div>
                                        */}
                                    {(gatturiAssessmentId || enableMansionPriceAssessment) && (
                                        <>
                                            <div className="Home-button-container">
                                                <button type="button" className="Home-button" onClick={() => {
                                                    analytics.trackButtonClick(BUTTON_NAMES.MANSION_ASSESSMENT, BUTTON_NAMES.MANSION_ASSESSMENT);
                                                    enableMansionPriceAssessment
                                                        ? setViewMode(VIEW_MODES.MANSION_ASSESSMENT)
                                                        : clickGetGatturiAssessmentReport();
                                                }}>
                                                    ガッツリ査定
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    <div className="Notifications-container">
                                        <div className="Notifications-label">
                                            担当者からのメッセージ
                                        </div>
                                        <div className="Notifications" style={{minHeight: '300px'}}>
                                            {memos.length > 0 ? (
                                                <>
                                                    {memos.map((memo, index) => (
                                                        <>
                                                            {memo.message} <br/>
                                                            {memo.updatedDate} <br/>
                                                            <br/>
                                                        </>
                                                    ))}
                                                </>
                                            ) : (
                                                <>
                                                    メッセージはありません
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {viewMode === VIEW_MODES.REPORT && (
                            <>
                                <div className="App-header-dark">
                                    <img src={backButton} className="Back-button" alt="back-button" onClick={() => {
                                        analytics.trackButtonClick(BUTTON_NAMES.BACK_TO_HOME, BUTTON_NAMES.BACK_TO_HOME);
                                        setViewMode(VIEW_MODES.HOME);
                                    }}/>
                                    <span className="Page-title">相場レポート</span>
                                </div>
                                <div className="App-body">
                                    <StreetViewMap latitude={sobaReportItem.propertyInfo.latitude}
                                                   longitude={sobaReportItem.propertyInfo.longitude}
                                                   heading={sobaReportItem.propertyInfo.panoHeading} panoId={panoId}/>
                                    <div className="Property-information-container">
                                        <div className="Property-name">
                                            {sobaReportItem.propertyInfo.name}
                                        </div>
                                        <div className="Property-list-group">
                                            <div>
                                                住居表示:&nbsp;{sobaReportItem.mansionBasicInfo.address}
                                            </div>
                                            <div>
                                                交通:&nbsp;{(sobaReportItem.mansionBasicInfo.railroadTrackName).trim()}&nbsp;{sobaReportItem.mansionBasicInfo.nearestStation1 ? sobaReportItem.mansionBasicInfo.nearestStation1 + '駅' : ''}&nbsp;{sobaReportItem.mansionBasicInfo.distance1 ? sobaReportItem.mansionBasicInfo.distance1 : ''}
                                            </div>
                                            <div>
                                                分譲会社:&nbsp;{(sobaReportItem.mansionBasicInfo.salesCompany).trim()}
                                            </div>
                                            <div>
                                                施工会社:&nbsp;{(sobaReportItem.mansionBasicInfo.contractor).trim()}
                                            </div>
                                            <div>
                                                建築年月:&nbsp;{sobaReportItem.mansionBasicInfo.yearBuilt}/{(sobaReportItem.mansionBasicInfo.monthBuilt).toString().padStart(2, '0')}&nbsp;総戸数:&nbsp;{sobaReportItem.mansionBasicInfo.totalUnits}戸&nbsp;建築階数:&nbsp;{sobaReportItem.mansionBasicInfo.numberOfFloorsOfBuilding}階建
                                            </div>
                                            <div>
                                                土地権利:&nbsp;{sobaReportItem.mansionBasicInfo.propertyRights}&nbsp;エレベーター:&nbsp;{sobaReportItem.mansionBasicInfo.isExistElevator ? '有り' : '無し'}&nbsp;構造:&nbsp;{sobaReportItem.mansionBasicInfo.structure}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="Property-overview-container">
                                        <div className="Property-title">マンション概要</div>
                                        <div className="Property-overview-label">住居表示</div>
                                        <div
                                            className="Property-overview-value">{sobaReportItem.propertySummary.address}</div>
                                        <div className="Property-overview-label">売主</div>
                                        <div
                                            className="Property-overview-value">{sobaReportItem.propertySummary.seller}</div>
                                        <div className="Property-overview-label">施工</div>
                                        <div
                                            className="Property-overview-value">{sobaReportItem.propertySummary.construction}</div>
                                        <div className="Property-overview-row">
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">建築年月</div>
                                                <div
                                                    className="Property-overview-value-half">{sobaReportItem.propertySummary.completionDate}</div>
                                            </div>
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">土地権利</div>
                                                <div
                                                    className="Property-overview-value-half">{sobaReportItem.propertySummary.propertyRights}</div>
                                            </div>
                                        </div>
                                        <div className="Property-overview-label">構造</div>
                                        <div
                                            className="Property-overview-value">{sobaReportItem.propertySummary.buildingStructure}</div>
                                        <div className="Property-overview-row">
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">建物階数</div>
                                                <div
                                                    className="Property-overview-value-half">{sobaReportItem.propertySummary.floorNumber}階
                                                </div>
                                            </div>
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">総戸数</div>
                                                <div
                                                    className="Property-overview-value-half">{sobaReportItem.propertySummary.totalUnits}戸
                                                </div>
                                            </div>
                                        </div>
                                        <div className="Property-overview-row">
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">平均専有</div>
                                                <div
                                                    className="Property-overview-value-half">{sobaReportItem.propertySummary.area}㎡
                                                </div>
                                            </div>
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">分譲単価</div>
                                                <div
                                                    className="Property-overview-value-half">{sobaReportItem.propertySummary.unitPrice ? sobaReportItem.propertySummary.unitPrice + '万円/坪' : ''}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    <div className="Property-price-container">
                                        <div className="Property-title">対象マンション中古売買相場</div>
                                        <div className="Property-description">直近10年の売・取引事例を時点修正済
                                        </div>
                                        <div className="Property-pricebox">
                                            <div className="Property-pricebox-inner">
                                                <div className="Property-pricebox-1">
                                                    <div className="Property-pricebox-label">中央値</div>
                                                    <div className="Property-pricebox-value">
                                                        <span
                                                            className="Property-pricebox-value-count">{sobaReportItem.targetMansionResaleMarket.medianPrice}</span>
                                                        <span
                                                            className="Property-pricebox-value-unit">万円/坪</span>
                                                    </div>
                                                </div>
                                                <div className="Property-pricebox-2">
                                                    <div className="Property-pricebox-label">中心価格帯</div>
                                                    <div className="Property-pricebox-value">
                                                            <span
                                                                className="Property-pricebox-value-count">{sobaReportItem.targetMansionResaleMarket.centralPriceRangeLower}-{sobaReportItem.targetMansionResaleMarket.centralPriceRangeUpper}</span>
                                                        <span
                                                            className="Property-pricebox-value-unit">万円/坪</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="Property-chart-container">
                                            <div className="Property-chart-yaxis-label">
                                                事例数
                                            </div>
                                            <div className="Property-chart">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        // width={500}
                                                        // height={300}
                                                        data={data}
                                                        margin={{
                                                            top: 0,
                                                            right: 0,
                                                            left: 0,
                                                            bottom: 0,
                                                        }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3"/>
                                                        <XAxis dataKey="price"/>
                                                        <YAxis/>
                                                        {/*<Tooltip />*/}
                                                        <Bar dataKey="uv" fill="#0070F4"/>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                            <div className="Property-chart-xaxis-label">
                                                坪単価(万円)
                                            </div>
                                        </div>
                                    </div>
                                    <hr/>
                                    {(sobaReportItem.similarMansionResaleMarket.length > 0) && (
                                        <>
                                            <div className="Property-near-container">
                                                <div className="Property-title">周辺類似マンション中古売買相場</div>
                                                <div className="Property-near-table-container">
                                                    <div className="Property-near-table-header">
                                                        <div className="Property-near-table-header-1">指定</div>
                                                        <div className="Property-near-table-header-2">名称</div>
                                                        <div className="Property-near-table-header-3">竣工年月 / 規模
                                                        </div>
                                                        <div className="Property-near-table-header-4">中央値</div>
                                                    </div>
                                                    <div className="Property-near-table-body target-property">
                                                        <div className="Property-near-table-body-1">●</div>
                                                        <div
                                                            className="Property-near-table-body-2">{sobaReportItem.similarMansionResaleMarket[0].locationName}
                                                        </div>
                                                        <div className="Property-near-table-body-3">
                                                            {sobaReportItem.similarMansionResaleMarket[0].completionDate}<br/>
                                                            {sobaReportItem.similarMansionResaleMarket[0].scale}
                                                        </div>
                                                        <div className="Property-near-table-body-4">
                                                            <span
                                                                className="Property-near-table-value-count">{sobaReportItem.similarMansionResaleMarket[0].medianPrice}</span>
                                                            <span className="Property-near-table-value-unit">万円</span>
                                                        </div>
                                                    </div>
                                                    <div className="Property-near-table-body near-property">
                                                        <div
                                                            className="Property-near-table-body-1 near-property-color">●
                                                        </div>
                                                        <div
                                                            className="Property-near-table-body-2">{sobaReportItem.similarMansionResaleMarket[1].locationName}
                                                        </div>
                                                        <div className="Property-near-table-body-3">
                                                            {sobaReportItem.similarMansionResaleMarket[1].completionDate}<br/>
                                                            {sobaReportItem.similarMansionResaleMarket[1].scale}
                                                        </div>
                                                        <div className="Property-near-table-body-4 near-property-color">
                                                            <span
                                                                className="Property-near-table-value-count">{sobaReportItem.similarMansionResaleMarket[1].medianPrice}</span>
                                                            <span className="Property-near-table-value-unit">万円</span>
                                                        </div>
                                                    </div>

                                                    <div className="Property-near-table-body near-property">
                                                        <div
                                                            className="Property-near-table-body-1 near-property-color">●
                                                        </div>
                                                        <div
                                                            className="Property-near-table-body-2">{sobaReportItem.similarMansionResaleMarket[2].locationName}
                                                        </div>
                                                        <div className="Property-near-table-body-3">
                                                            {sobaReportItem.similarMansionResaleMarket[2].completionDate}<br/>
                                                            {sobaReportItem.similarMansionResaleMarket[2].scale}
                                                        </div>
                                                        <div className="Property-near-table-body-4 near-property-color">
                                                            <span
                                                                className="Property-near-table-value-count">{sobaReportItem.similarMansionResaleMarket[2].medianPrice}</span>
                                                            <span className="Property-near-table-value-unit">万円</span>
                                                        </div>
                                                    </div>

                                                    <div className="Property-near-table-body near-property">
                                                        <div
                                                            className="Property-near-table-body-1 near-property-color">●
                                                        </div>
                                                        <div
                                                            className="Property-near-table-body-2">{sobaReportItem.similarMansionResaleMarket[3].locationName}
                                                        </div>
                                                        <div className="Property-near-table-body-3">
                                                            {sobaReportItem.similarMansionResaleMarket[3].completionDate}<br/>
                                                            {sobaReportItem.similarMansionResaleMarket[3].scale}
                                                        </div>
                                                        <div className="Property-near-table-body-4 near-property-color">
                                                            <span
                                                                className="Property-near-table-value-count">{sobaReportItem.similarMansionResaleMarket[3].medianPrice}</span>
                                                            <span className="Property-near-table-value-unit">万円</span>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                            <hr/>
                                        </>
                                    )}
                                    <div className="Property-near-container" style={{marginBottom: '100px'}}>
                                        <div className="Property-title">近隣の中古マンション価格推移</div>
                                        <div
                                            className="Property-description">{sobaReportItem.neighborResalePriceTrend.description}</div>
                                        <div className="Property-line-chart-container">
                                            <ResponsiveContainer width="200%" height="98%">
                                                <LineChart
                                                    width={500}
                                                    height={300}
                                                    data={data2}
                                                    margin={{
                                                        top: 40,
                                                        right: 0,
                                                        left: 0,
                                                        bottom: 0,
                                                    }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3"/>
                                                    <XAxis dataKey="name" padding={{left: 30, right: 30}}/>
                                                    <YAxis/>
                                                    <Legend/>
                                                    <Line type="basis" legendType="square"
                                                          dot={<CustomizedDotForLineChart labelPosition={'up'}/>}
                                                          strokeWidth={3}
                                                          dataKey={sobaReportItem.neighborResalePriceTrend.stationTransition.name}
                                                          stroke="#ff245b"/>
                                                    <Line type="basis" legendType="circle"
                                                          dot={<CustomizedDotForLineChart labelPosition={'down'}/>}
                                                          strokeWidth={3}
                                                          dataKey={sobaReportItem.neighborResalePriceTrend.wardTransition.name}
                                                          stroke="#89bdde"/>
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {viewMode === VIEW_MODES.POTITTO && (
                            <>
                                <div className="App-header-dark">
                                    <img src={backButton} className="Back-button" alt="back-button" onClick={() => {
                                        analytics.trackButtonClick(BUTTON_NAMES.BACK_TO_HOME, BUTTON_NAMES.BACK_TO_HOME);
                                        setViewMode(VIEW_MODES.HOME);
                                    }}/>
                                    <span className="Page-title">ポチッとAI結果</span>
                                </div>
                                <div className="App-body">
                                    <div className="Potitto-result-header1-container">
                                        <div className="Potitto-result-header1-label">
                                            AI推定価格
                                        </div>
                                        <div className="Potitto-result-header1-result-value">
                                            {clickAiItem.aiEstimatedPrice.toLocaleString()}
                                        </div>
                                        <div className="Potitto-result-header1-result-unit">
                                            万円
                                        </div>
                                        <div className="Potitto-result-header1-result-description-container">
                                            <div
                                                className="Potitto-result-header1-result-description-tsubo-container">
                                                <span className="Potitto-result-header1-result-description-tsubo-value">
                                                    {clickAiItem.pricePerTsubo.toLocaleString()}
                                                </span>
                                                <span
                                                    className="Potitto-result-header1-result-description-tsubo-unit">
                                                    万円/坪
                                                </span>
                                            </div>
                                            <div
                                                className="Potitto-result-header1-result-description-heibei-container">
                                                <span
                                                    className="Potitto-result-header1-result-description-heibei-value">
                                                    {clickAiItem.pricePerSquareMeters.toLocaleString()}
                                                </span>
                                                <span
                                                    className="Potitto-result-header1-result-description-heibei-unit">
                                                    万円/㎡
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="Potitto-result-triangle-line">
                                        <div className="Potitto-result-triangle">
                                        </div>
                                    </div>
                                    <div className="Potitto-result-header2-container">
                                        <div className="Potitto-result-header2-tanka-container">
                                            <div className="Potitto-result-header2-tanka-label">
                                                標準住戸単価
                                            </div>
                                            <div className="Potitto-result-header2-tanka-result">
                                                <div className="Potitto-result-header2-tanka-value">
                                                    {clickAiItem.pricePerSquareMetersOfStandardUnitPrice.toLocaleString()}
                                                </div>
                                                <div className="Potitto-result-header2-tanka-unit">
                                                    円/㎡
                                                </div>
                                            </div>
                                            <div className="Potitto-result-header2-tanka-description">
                                                ({clickAiItem.pricePerTsuboOfStandardUnitPrice.toLocaleString()} 万円/坪)
                                            </div>
                                        </div>
                                        <div className="Potitto-result-header2-operator">x</div>
                                        <div className="Potitto-result-header2-kakusa-container">
                                            <div className="Potitto-result-header2-kakusa-label">
                                                個別格差
                                            </div>
                                            <div className="Potitto-result-header2-kakusa-value">
                                                {clickAiItem.individualDifference} / 100
                                            </div>
                                        </div>
                                        <div className="Potitto-result-header2-operator">x</div>
                                        <div className="Potitto-result-header2-menseki-container">
                                            <div className="Potitto-result-header2-menseki-label">
                                                専有面積
                                            </div>
                                            <div className="Potitto-result-header2-menseki-value">
                                                {(clickAiItem.exclusiveArea).toFixed(1)} ㎡
                                            </div>
                                        </div>
                                        <div className="Potitto-result-header2-operator">≒</div>
                                        <div className="Potitto-result-header2-kakaku-container">
                                            <div className="Potitto-result-header2-kakaku-label">
                                                AI推定価格
                                            </div>
                                            <div className="Potitto-result-header2-kakaku-result">
                                                <div className="Potitto-result-header2-kakaku-value">
                                                    {clickAiItem.aiEstimatedPriceOfNearlyEqual.toLocaleString()}
                                                </div>
                                                <div className="Potitto-result-header2-kakaku-unit">
                                                    万円
                                                </div>
                                            </div>
                                            <div className="Potitto-result-header2-kakaku-description">
                                                ({clickAiItem.aiEstimatedPriceOfPricePerTsubo.toLocaleString()} 万円/坪)
                                            </div>
                                        </div>
                                    </div>
                                    <StreetViewMap latitude={clickAiItem.propertyInfo.latitude}
                                                   longitude={clickAiItem.propertyInfo.longitude}
                                                   heading={clickAiItem.propertyInfo.panoHeading} panoId={panoId}/>
                                    <div className="Property-information-container">
                                        <div className="Property-name">
                                            {clickAiItem.propertyInfo.name}
                                        </div>
                                        <div className="Property-list-group">
                                            <div>
                                                住居表示:&nbsp;{clickAiItem.mansionBasicInfo.address}
                                            </div>
                                            <div>
                                                交通:&nbsp;{(clickAiItem.mansionBasicInfo.railroadTrackName).trim()}&nbsp;{clickAiItem.mansionBasicInfo.nearestStation1 ? clickAiItem.mansionBasicInfo.nearestStation1 + '駅' : ''}&nbsp;{clickAiItem.mansionBasicInfo.distance1 ? clickAiItem.mansionBasicInfo.distance1 : ''}
                                            </div>
                                            <div>
                                                分譲会社:&nbsp;{(clickAiItem.mansionBasicInfo.salesCompany).trim()}
                                            </div>
                                            <div>
                                                施工会社:&nbsp;{(clickAiItem.mansionBasicInfo.contractor).trim()}
                                            </div>
                                            <div>
                                                建築年月:&nbsp;{clickAiItem.mansionBasicInfo.yearBuilt}/{(clickAiItem.mansionBasicInfo.monthBuilt).toString().padStart(2, '0')}&nbsp;総戸数:&nbsp;{clickAiItem.mansionBasicInfo.totalUnits}戸&nbsp;建築階数:&nbsp;{clickAiItem.mansionBasicInfo.numberOfFloorsOfBuilding}階建
                                            </div>
                                            <div>
                                                土地権利:&nbsp;{clickAiItem.mansionBasicInfo.propertyRights}&nbsp;エレベーター:&nbsp;{clickAiItem.mansionBasicInfo.isExistElevator ? '有り' : '無し'}&nbsp;構造:&nbsp;{clickAiItem.mansionBasicInfo.structure}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="Property-overview-container">
                                        <div className="Property-title">マンション概要</div>
                                        <div className="Property-overview-label">住居表示</div>
                                        <div
                                            className="Property-overview-value">{clickAiItem.propertySummary.address}</div>
                                        <div className="Property-overview-label">構造</div>
                                        <div
                                            className="Property-overview-value">{clickAiItem.propertySummary.buildingStructure}</div>
                                        <div className="Property-overview-row">
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">階数</div>
                                                <div
                                                    className="Property-overview-value-half">{clickAiItem.floor}階
                                                </div>
                                            </div>
                                            <div className="Property-overview-half">
                                                <div className="Property-overview-label-half">部屋番号</div>
                                                <div
                                                    className="Property-overview-value-half">{clickAiItem.roomNumber}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="Property-overview-label">総戸数</div>
                                        <div
                                            className="Property-overview-value">{clickAiItem.propertySummary.totalUnits}戸
                                        </div>
                                        <div className="Property-overview-label">売主</div>
                                        <div
                                            className="Property-overview-value">{clickAiItem.propertySummary.seller}</div>
                                    </div>
                                    <PropertyMap latitude={clickAiItem.propertyInfo.latitude}
                                                 longitude={clickAiItem.propertyInfo.longitude}/>
                                    <div className="Property-warn-note-container">
                                        <ul>
                                            <li>本データは「不動産の鑑定評価に関する法律」に基づく不動産鑑定評価書や「宅地建物取引業法」に基づく、媒介価格の根拠として作成する査定書ではありません。
                                                また売却価格を保証するものではありません。
                                            </li>
                                            <li>AI推定価格は現地調査を行うことなく算出しているため、物件の状態、個別事情により異なる場合があります。また出力日現在の価格であり、法令改正、市場動向等により変化が
                                                生じる場合があります。
                                            </li>
                                            <li>本資料の内容について、複製・第三者への開示などはご遠慮下さい。</li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}

                        {viewMode === VIEW_MODES.MANSION_ASSESSMENT && (
                            <>
                                <div className="App-header-dark">
                                    <img src={backButton} className="Back-button" alt="back-button" onClick={() => {
                                        analytics.trackButtonClick(BUTTON_NAMES.BACK_TO_HOME, BUTTON_NAMES.BACK_TO_HOME);
                                        setViewMode(VIEW_MODES.HOME);
                                    }}/>
                                    <span className="Page-title">ガッツリ査定</span>
                                </div>
                                <div className="App-body">
                                    <div style={{backgroundColor: 'white', paddingTop: '5vh', paddingBottom: '5vh'}}>
                                        <Worker
                                            workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                                            <Viewer fileUrl={mansionAssessmentItem}/>
                                        </Worker>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </HelmetProvider>
    );

    function clickGetGatturiAssessmentReport() {
        setLoading(true);

        const search_query = gql`
            query {
                getGatturiAssessmentReport(
                    assessmentId: "${gatturiAssessmentId}",
                    projectId: ${noteRequestData.note_id},
                    authCode: "${authCodeData}"
                ) {
                    common {
                        userToken
                        statusCode
                        message
                    }
                    pdfId
                    pdfUrl
                }
            }
        `;
        apolloClient
            .query({
                query: search_query
            })
            .then((result) => {
                const pdfUrl = result.data.getGatturiAssessmentReport.pdfUrl;
                if (pdfUrl) {
                    // Base64をバイナリに変換
                    const byteCharacters = atob(pdfUrl);
                    const byteNumbers = new Uint8Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }

                    // バイナリからBlobを作成
                    const mansionPriceAssessmentBlob = new Blob([byteNumbers], {type: 'application/pdf'});
                    // BlobからURLを生成
                    const mansionAssessmentResultUrl = URL.createObjectURL(mansionPriceAssessmentBlob);
                    setMansionAssessmentItem(mansionAssessmentResultUrl);

                    setLoading(false);
                    // 査定書を表示する
                    setViewMode(VIEW_MODES.MANSION_ASSESSMENT)
                }
            })
            .catch((error) => {
                setLoading(false);
                console.log('エラー', error);
            });
    }

    function clickAuth() {
        setAuthWarnMessage('');
        setLoading(true);
        const authCode = document.getElementById('Auth-number-input').value;
        const pathname = window.location.pathname.split('/');
        const noteRequestBase64 = pathname[pathname.length - 1];

        let noteRequest;
        try {
            const decodedString = atob(noteRequestBase64);
            noteRequest = JSON.parse(decodedString);
        } catch (error) {
            console.error('Invalid JSON or Base64:', error);
            setAuthWarnMessage('無効なURLです。');
            setLoading(false);
            return;
        }
        setNoteRequestData(noteRequest);
        setAuthCodeData(authCode);

        const search_query = gql`
            query {
                getWebItem(
                    projectId: ${noteRequest.note_id},
                    buildingCode: "${noteRequest.buildingCode}",
                    authCode: "${authCode}"
                ) {
                    common {
                        userToken
                        statusCode
                        message
                    }
                    priceReport
                    clickAi
                    mansionPriceAssessment
                    gatturiAssessmentId
                    clientShareMemo {
                        message
                        updatedDate
                    }
                    panoId
                }
            }
        `;
        apolloClient
            .query({
                query: search_query
            })
            .then((result) => {

                setLoading(false);
                if (result.data.getWebItem.common.statusCode !== 200) {

                    setAuthWarnMessage(result.data.getWebItem.common.message);

                } else {

                    setAuthWarnMessage('');
                    setViewMode(VIEW_MODES.HOME);

                    const sobaReportData = JSON.parse(result.data.getWebItem.priceReport);
                    const clickAiData = JSON.parse(result.data.getWebItem.clickAi);
                    const mansionPriceAssessmentData = result.data.getWebItem.mansionPriceAssessment;
                    setSobaReportItem(sobaReportData);
                    setClickAiItem(clickAiData);
                    setMemos(result.data.getWebItem.clientShareMemo);
                    setGatturiAssessmentId(result.data.getWebItem.gatturiAssessmentId);
                    setPanoId(result.data.getWebItem.panoId);

                    if (sobaReportData) {
                        setEnableMarketReport(true);

                        const priceReport = JSON.parse(result.data.getWebItem.priceReport);

                        // 対象マンション中古売買相場グラフ
                        let data_tmp = [];
                        const transactionCase = priceReport.targetMansionResaleMarket.transactionCase;
                        // console.log(transactionCase)
                        for (let i = 0; i < transactionCase.length; i++) {
                            data_tmp.push({
                                price: transactionCase[i].tsuboPrice,
                                uv: transactionCase[i].caseCount,
                            });
                        }
                        setData(data_tmp);

                        // 近隣の中古マンション価格推移
                        let data2_tmp = [];
                        const neighborResalePriceTrend = priceReport.neighborResalePriceTrend;

                        for (let i = 0; i < neighborResalePriceTrend.year.length; i++) {
                            data2_tmp.push({
                                name: neighborResalePriceTrend.year[i],
                                [neighborResalePriceTrend.stationTransition.name]: neighborResalePriceTrend.wardTransition.tsuboPrice[i],
                                [neighborResalePriceTrend.wardTransition.name]: neighborResalePriceTrend.stationTransition.tsuboPrice[i],
                            });
                        }
                        setData2(data2_tmp);
                    } else {
                        setEnableMarketReport(false);
                    }

                    if (clickAiData) {
                        setEnableClickAi(true);
                    } else {
                        setEnableClickAi(false);
                    }

                    if (mansionPriceAssessmentData) {
                        setEnableMansionPriseAssessment(true);

                        // Base64をバイナリに変換
                        const byteCharacters = atob(mansionPriceAssessmentData);
                        const byteNumbers = new Uint8Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }

                        // バイナリからBlobを作成
                        const mansionPriceAssessmentBlob = new Blob([byteNumbers], {type: 'application/pdf'});
                        // BlobからURLを生成
                        const mansionAssessmentResultUrl = URL.createObjectURL(mansionPriceAssessmentBlob);
                        setMansionAssessmentItem(mansionAssessmentResultUrl);

                    } else {
                        setEnableMansionPriseAssessment(false);
                        setMansionAssessmentItem(mansionPriceAssessmentData);
                    }


                }
            })
            .catch((error) => {
                setLoading(false);
                console.log(error);
            });
    }
}


export default Home;